import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Brain, Fingerprint, ShieldCheck, Server, ArrowRight } from 'lucide-react';
import { DEFAULT_LANGUAGE } from '../../lib/i18n';
import { generateContactEmailLink } from '../../lib/emailTemplates';

const featureIcons = [Brain, Fingerprint, ShieldCheck, Server];

interface FeatureItem { title: string; desc: string }

export function CustomFinetuneSection() {
  const { t } = useTranslation('home');
  const { lang } = useParams<{ lang?: string }>();
  const currentLang = lang || DEFAULT_LANGUAGE;

  const features = (t('customFinetune.features', { returnObjects: true }) as FeatureItem[]) || [];

  return (
    <section id="custom-finetune" className="relative py-20 sm:py-28 overflow-hidden">
      {/* gradient scrim so the NeuralBackground shows through softly */}
      <div className="absolute inset-0 bg-linear-to-b from-transparent via-background/40 to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl border border-blue-500/25 bg-linear-to-br from-blue-500/10 via-indigo-500/5 to-orange-500/5 dark:from-blue-500/12 dark:via-indigo-500/8 dark:to-orange-500/8 backdrop-blur-sm p-8 sm:p-12 lg:p-16"
        >
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary mb-4">
              <span className="h-px w-8 bg-linear-to-r from-orange-500 to-primary rounded-full" />
              {t('customFinetune.kicker')}
              <span className="h-px w-8 bg-linear-to-l from-orange-500 to-primary rounded-full" />
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-5 leading-tight text-balance">
              {t('customFinetune.title')}
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('customFinetune.subtitle')}
            </p>
          </div>

          {/* Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
            {features.map((f, i) => {
              const Icon = featureIcons[i] ?? Brain;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex flex-col gap-3 rounded-2xl border border-border/60 bg-card/85 dark:bg-white/8 backdrop-blur-sm p-6"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-white shadow-lg shadow-primary/25">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="text-base font-bold text-foreground leading-snug">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center gap-4 text-center">
            <a
              href={generateContactEmailLink(currentLang)}
              className="group inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-primary to-accent px-8 py-4 text-base font-semibold text-white shadow-lg shadow-primary/30 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-primary/40 active:scale-95"
            >
              {t('customFinetune.cta')}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </a>
            <p className="text-sm text-muted-foreground">{t('customFinetune.ctaNote')}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
